import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./Overview.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  Marker,
  Popup,
  FeatureGroup,
} from "react-leaflet";
import { EditControl } from "react-leaflet-draw";
import L from "leaflet";
import "chart.js/auto";
import moment from 'moment-timezone';
import { MapHomeControl, MapInfoControl, MapLegendControl } from "../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

const PopupContent = ({ facilities = [], layerId, showHospitals, setShowHospitals, onViewDetails }) => {
  const handleCheckboxChange = (e) => {
    setShowHospitals(prev => ({
      ...prev,
      [layerId]: e.target.checked
    }));
  };

  return (
    <div style={{ padding: '10px', minWidth: '200px', maxHeight: '300px', overflowY: 'auto' }}>
      <h3>Emergency Services</h3>
      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          onChange={handleCheckboxChange}
          style={{ marginRight: '8px', width: '16px', height: '16px' }}
        />
        <span>Hospitals: {facilities.length}</span>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          style={{ marginRight: '8px', width: '16px', height: '16px' }}
        />
        <span>Policestation: </span>
      </label>
      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          style={{ marginRight: '8px', width: '16px', height: '16px' }}
        />
        <span>Fire Station: </span>
      </label>
    </div>
  );
};

const Overview = () => {
  const [legendVisible, setLegendVisible] = useState(false);
  const [geojsonData, setGeojsonData] = useState(null);
  const [mapActivity, setMapActivity] = useState({
    blackspots: [],
    reported_accidents: [],
    reported_road_conditions: [],
  });
  const [healthFacilities, setHealthFacilities] = useState(null);
  const [showHospitals, setShowHospitals] = useState({});
  const [loadingActivity, setLoadingActivity] = useState(true);
  const legendRef = useRef(null);
  const drawRef = useRef(null);
  const featureGroupRef = useRef(null);

  useEffect(() => {
    fetch("/DATA/OSMbased_adm0_boundary.json")
      .then((response) => response.json())
      .then((data) => setGeojsonData(data))
      .catch((error) => console.error("Error loading boundary data:", error));

    fetch(`${backendUrl}roadaccident/recent_map_activity/`)
      .then((response) => response.json())
      .then((data) => setMapActivity(data))
      .catch((error) => console.error("Error loading recent map activity:", error))
      .finally(() => setLoadingActivity(false));

    fetch("/Healthfacilities.geojson")
      .then((response) => response.json())
      .then((data) => setHealthFacilities(data))
      .catch((error) => console.error("Error loading health facilities:", error));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (legendRef.current && !legendRef.current.contains(event.target)) {
        setLegendVisible(false);
      }
    };

    if (legendVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [legendVisible]);

  const geoJsonStyle = {
    color: "black",
    weight: 2,
    fillOpacity: 0,
  };

  const position = [-9.1747, 125.7275];

  const hasActiveRecords =
    mapActivity.blackspots.length > 0 ||
    mapActivity.reported_accidents.length > 0 ||
    mapActivity.reported_road_conditions.length > 0;

  const blackspotIcon = L.icon({ iconUrl: "/Roadaccidenticons/black.png", iconSize: [30, 30] });
  const reportedAccidentIcon = L.icon({ iconUrl: "/Roadaccidenticons/reported.gif", iconSize: [36, 36] });
  const reportedRoadIcon = L.icon({ iconUrl: "/Roadaccidenticons/red.png", iconSize: [22, 22] });

  const hospitalIcon = L.icon({
    iconUrl: "/Roadaccidenticons/red.png",
    iconSize: [10, 10],
  });

  const getFacilitiesInShape = (layer) => {
    if (!healthFacilities || !healthFacilities.features) return [];
    const bounds = layer.getBounds();
    return healthFacilities.features.filter(feature => {
      const [lng, lat] = feature.geometry.coordinates;
      return bounds.contains([lat, lng]);
    });
  };

  const handleViewDetails = (facilities) => {
    console.log("Viewing details for facilities:", facilities);
  };

  const renderPopupContent = (layer, facilities) => {
    const layerId = layer._leaflet_id;
    const container = document.createElement('div');
    ReactDOM.render(
      <PopupContent
        facilities={facilities}
        layerId={layerId}
        showHospitals={showHospitals}
        setShowHospitals={setShowHospitals}
        onViewDetails={handleViewDetails}
      />,
      container
    );
    return container;
  };

  const handleDrawCreated = (e) => {
    const layer = e.layer;
    const facilities = getFacilitiesInShape(layer);
    layer.bindPopup(renderPopupContent(layer, facilities)).openPopup();
    console.log('Created shape:', layer.toGeoJSON());
  };

  const handleDrawEdited = (e) => {
    e.layers.eachLayer((layer) => {
      const facilities = getFacilitiesInShape(layer);
      layer.setPopupContent(renderPopupContent(layer, facilities));
      console.log('Edited shape:', layer.toGeoJSON());
    });
  };

  const handleDrawDeleted = (e) => {
    const deletedIds = [];
    e.layers.eachLayer((layer) => {
      deletedIds.push(layer._leaflet_id);
      console.log('Deleted shape:', layer.toGeoJSON());
    });
    setShowHospitals(prev => {
      const newState = { ...prev };
      deletedIds.forEach(id => delete newState[id]);
      return newState;
    });
  };

  return (
    <div className="overviewdashboard">
      <div className="overviewdashboardpart1">
        <div className="map-section">
          <div className="map-container">
            <MapContainer center={position} zoom={8} className="leaflet-map">
              <MapHomeControl />
              <MapInfoControl text="Overview of reported road accidents. Click a marker to view accident details." />
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution="© OpenStreetMap contributors"
              />
              {geojsonData && <GeoJSON data={geojsonData} style={geoJsonStyle} />}

              {!loadingActivity && !hasActiveRecords && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    zIndex: 1000,
                    background: "rgba(255, 255, 255, 0.92)",
                    padding: "10px 18px",
                    borderRadius: "8px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#555",
                    pointerEvents: "none",
                  }}
                >
                  No active records
                </div>
              )}

              <FeatureGroup ref={featureGroupRef}>
                <EditControl
                  ref={drawRef}
                  position="topright"
                  onCreated={handleDrawCreated}
                  onEdited={handleDrawEdited}
                  onDeleted={handleDrawDeleted}
                  draw={{
                    polyline: false,
                    polygon: {
                      allowIntersection: false,
                      drawError: {
                        color: '#e1e100',
                        message: 'Polygons cannot intersect!'
                      },
                      shapeOptions: {
                        color: '#ff0000',
                        weight: 2,
                        opacity: 0.8,
                        fillOpacity: 0.3
                      }
                    },
                    circle: {
                      shapeOptions: {
                        color: '#00ff00',
                        weight: 2,
                        opacity: 0.8,
                        fillOpacity: 0.3
                      }
                    },
                    rectangle: {
                      shapeOptions: {
                        color: '#0000ff',
                        weight: 2,
                        opacity: 0.8,
                        fillOpacity: 0.3
                      }
                    },
                    marker: false,
                    circlemarker: false
                  }}
                  edit={{ edit: true, remove: true }}
                />
              </FeatureGroup>

              {mapActivity.blackspots.map((spot, index) => (
                <Marker
                  key={`blackspot-${index}`}
                  position={[parseFloat(spot.latitude), parseFloat(spot.longitude)]}
                  icon={blackspotIcon}
                >
                  <Popup>
                    <h3>Black Spot</h3>
                    Location: {spot.location}<br />
                    Road Type: {spot.roadtype}<br />
                    Date and Time: {moment.utc(spot.datetime).tz('Asia/Dili').format('MMMM DD, YYYY [at] h:mm A')}<br />
                    Accidents: {spot.numberofaccidents}, Injured: {spot.numberofinjured}, Fatal: {spot.numberoffatal}
                  </Popup>
                </Marker>
              ))}

              {mapActivity.reported_accidents.map((accident, index) => (
                <Marker
                  key={`reported-accident-${index}`}
                  position={[parseFloat(accident.latitude), parseFloat(accident.longitude)]}
                  icon={reportedAccidentIcon}
                >
                  <Popup className="custom-popup-class">
                    <h2 className="tbTit">Reported Accident</h2>
                    Location: {accident.location}<br />
                    Date and Time: {moment.utc(accident.datetime).tz('Asia/Dili').format('MMMM DD, YYYY [at] h:mm A')}<br />
                    Accident Type: {accident.accident_type}<br />
                    Advisory: {accident.advisory || 'N/A'}<br />
                    {accident.message}
                  </Popup>
                </Marker>
              ))}

              {mapActivity.reported_road_conditions.map((report, index) => (
                <Marker
                  key={`reported-road-${index}`}
                  position={[parseFloat(report.latitude), parseFloat(report.longitude)]}
                  icon={reportedRoadIcon}
                >
                  <Popup>
                    <h3>Reported Road Condition</h3>
                    Location: {report.location}<br />
                    Date and Time: {moment.utc(report.date_time).tz('Asia/Dili').format('MMMM DD, YYYY [at] h:mm A')}<br />
                    Issue: {report.issue}{report.other_issue ? ` (${report.other_issue})` : ''}<br />
                    {report.message}
                  </Popup>
                </Marker>
              ))}

              {healthFacilities && featureGroupRef.current && Object.entries(showHospitals).map(([layerId, show]) => {
                if (!show) return null;
                const layer = featureGroupRef.current?.getLayer(layerId);
                if (!layer) return null;
                const facilities = getFacilitiesInShape(layer);
                return facilities.map((facility, index) => {
                  const [lng, lat] = facility.geometry.coordinates;
                  return (
                    <Marker
                      key={`hospital-${layerId}-${index}`}
                      position={[lat, lng]}
                      icon={hospitalIcon}
                    >
                      <Popup>
                        <h3>{facility.properties.HF_NAME || 'Unknown'}</h3>
                        {Object.entries(facility.properties).map(([key, value]) => (
                          <div key={key}>
                            {key}: {value || 'Unknown'}
                          </div>
                        ))}
                      </Popup>
                    </Marker>
                  );
                });
              })}

              <MapLegendControl
                sections={[{
                  items: [
                    { label: "Reported Accident", icon: <img src="/Roadaccidenticons/reported.gif" alt="Icon" style={{ width: "18px", height: "18px" }} /> },
                    { label: "Black Spot", icon: <img src="/Roadaccidenticons/black.png" alt="Icon" style={{ width: "18px", height: "18px" }} /> },
                    { label: "Reported Road Condition", icon: <img src="/Roadaccidenticons/red.png" alt="Icon" style={{ width: "18px", height: "18px" }} /> },
                  ],
                }]}
              />
            </MapContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;